import { Link, useNavigate } from "@tanstack/react-router";
import { useAuthActions } from "@convex-dev/auth/react";
import { useMutation } from "convex/react";
import type { ReactNode } from "react";
import { LogOut } from "lucide-react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { Button, cn, useRun } from "@/components/ui";
import { Logo } from "../components/Logo";
import { siteLink } from "@/lib/site";

type Role = "admin" | "pm" | "viewer";

export interface Me {
  user: { email?: string; name?: string };
  tenant: { _id: Id<"tenants">; name: string } | null;
  role: Role | null;
  platformAdmin: boolean;
  billingEnabled: boolean;
  orgs: { tenantId: Id<"tenants">; name: string; role: Role }[];
  invites: { _id: Id<"invites">; tenantName: string; role: Role }[];
}

const ROLE_NAME: Record<Role, string> = { admin: "admin", pm: "project manager", viewer: "viewer" };

export function AppShell({ me, children }: { me: Me; children: ReactNode }) {
  const { signOut } = useAuthActions();
  const switchOrg = useMutation(api.account.switchOrg);
  const accept = useMutation(api.account.acceptInvite);
  const decline = useMutation(api.account.declineInvite);
  const navigate = useNavigate();
  const run = useRun();
  const link = "rounded px-2.5 py-1.5 text-sm text-[#cbd5e1] hover:bg-white/10 hover:text-white";
  const active = { className: cn(link, "bg-white/10 text-white") };
  return (
    <div className="flex h-full flex-col print:block print:h-auto">
      <header className="no-print flex h-12 shrink-0 items-center gap-4 bg-[#162a45] px-4 text-white">
        <Link to="/" aria-label="Mongoose EVM home" className="flex items-center">
          <Logo />
        </Link>
        {me.orgs.length > 1 ? (
          <select
            aria-label="Organisation"
            title="Switch organisation"
            className="rounded bg-transparent text-sm text-[#cbd5e1] hover:bg-white/10 [&>option]:text-slate-900"
            value={me.tenant?._id}
            onChange={(e) => run(async () => { await switchOrg({ tenantId: e.target.value as Id<"tenants"> }); await navigate({ to: "/" }); })}
          >
            {me.orgs.map((o) => (
              <option key={o.tenantId} value={o.tenantId}>
                {o.name}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-sm text-[#94a3b8]">{me.tenant?.name}</span>
        )}
        <nav className="ml-4 flex gap-1">
          <Link to="/" className={link} activeProps={active} activeOptions={{ exact: true }}>
            Projects
          </Link>
          <Link to="/library" search={{ tab: "milestones" }} className={link} activeProps={active}>
            Library
          </Link>
          {me.role === "admin" && (
            <Link to="/team" className={link} activeProps={active}>
              Team
            </Link>
          )}
          {me.role === "admin" && (me.billingEnabled || me.platformAdmin) && (
            <Link to="/billing" className={link} activeProps={active}>
              Billing
            </Link>
          )}
          {me.platformAdmin && (
            <Link to="/platform" className={link} activeProps={active}>
              Platform
            </Link>
          )}
        </nav>
        <div className="ml-auto flex items-center gap-3 text-sm text-[#cbd5e1]">
          <a href={siteLink("help")} target="_blank" rel="noreferrer" className="rounded px-2 py-1 hover:bg-white/10 hover:text-white">
            Help
          </a>
          <span>
            {me.user.email} <span className="text-[#64748b]">({me.role})</span>
          </span>
          <button className="rounded p-1.5 hover:bg-white/10" aria-label="Sign out" title="Sign out" onClick={() => void signOut()}>
            <LogOut className="size-4" />
          </button>
        </div>
      </header>
      {me.invites.map((i) => (
        <div key={i._id} className="no-print flex items-center gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          <span>
            <b>{i.tenantName}</b> invited you to join as {ROLE_NAME[i.role]}. You keep access to {me.tenant?.name ?? "your organisation"} and can switch between them at the top left.
          </span>
          <Button size="sm" variant="primary" onClick={() => run(async () => { await accept({ id: i._id }); await navigate({ to: "/" }); }, `Joined ${i.tenantName}`)}>
            Join
          </Button>
          <Button size="sm" variant="ghost" onClick={() => run(() => decline({ id: i._id }))}>
            Decline
          </Button>
        </div>
      ))}
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  );
}
